import { Box, Typography, Skeleton, useTheme } from "@mui/material";

interface ProductFeaturesProps {
  features?: string[];
  isLoading?: boolean;
}

export const ProductFeatures = ({
  features,
  isLoading = false,
}: ProductFeaturesProps) => {
  const theme = useTheme();
  // Show skeleton loader while loading and no data
  if (isLoading && (!features || features.length === 0)) {
    return (
      <Box
        sx={{
          marginBottom: { xs: 2, md: 3 },
          padding: { xs: 2.5, sm: 3, md: 3.5 },
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          borderRadius: 3,
          border: "1px solid #e8e8e8",
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          minWidth: "100%",
        }}
      >
        <Skeleton
          variant="text"
          sx={{
            fontSize: { xs: 16, md: 18 },
            fontWeight: 700,
            mb: { xs: 2, md: 2.5 },
            width: "40%",
          }}
        />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{
              height: { xs: 48, md: 54 },
              borderRadius: 2,
              mb: 1.5,
            }}
          />
        ))}
      </Box>
    );
  }

  // Don't show anything if not loading and no data
  if (!features || features.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        marginBottom: { xs: 2, md: 3 },
        padding: { xs: 2.5, sm: 3, md: 3.5 },
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        borderRadius: 3,
        border: "1px solid #e8e8e8",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: { xs: "4px", md: "5px" },
          background: `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
          borderRadius: "12px 0 0 12px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 1.25 },
          mb: { xs: 2, md: 2.5 },
          paddingLeft: { xs: 1, md: 1.5 },
        }}
      >
        <Box
          sx={{
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: { xs: 14, md: 16 },
            fontWeight: 600,
            boxShadow: `0 2px 8px ${theme.palette.secondary.main}4D`,
          }}
        >
          ✨
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: 16, md: 18 },
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Key Features
        </Typography>
      </Box>
      <Box
        component="ul"
        sx={{
          paddingLeft: { xs: 1.5, md: 2 },
          margin: 0,
          listStyle: "none",
          display: "grid",
          gap: { xs: 1.5, md: 1.75 },
        }}
      >
        {features.map((detail, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 1.5, md: 1.75 },
              padding: { xs: "12px 16px", md: "14px 18px" },
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              borderRadius: 2,
              border: `1px solid ${theme.palette.secondary.main}1A`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: `${theme.palette.secondary.main}0D`,
                borderColor: `${theme.palette.secondary.main}33`,
                transform: "translateX(2px)",
              },
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 6, md: 7 },
                height: { xs: 6, md: 7 },
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
                mt: { xs: 0.5, md: 0.6 },
                flexShrink: 0,
                boxShadow: `0 1px 3px ${theme.palette.secondary.main}4D`,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: { xs: 1.6, md: 1.7 },
                flex: 1,
                fontSize: { xs: 14, md: 15 },
                fontWeight: 500,
              }}
            >
              {detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
